import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";

const NAV_LINKS = [
    { href: "#studio", label: "Lo Studio" },
    { href: "#aree", label: "Aree di Competenza" },
    { href: "/calcolo-termini-impugnazione-licenziamento", label: "Scadenze", isPage: true },
    { href: "#articoli", label: "Articoli" },
    { href: "#faq", label: "FAQ" },
    { href: "#contatti", label: "Contatti" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const isHome = location.pathname === "/";
    // Pagine con hero scuro: l'header deve restare bianco trasparente come in home
    const hasDarkHero =
        isHome ||
        location.pathname.startsWith("/avvocato-lavoro-") ||
        location.pathname.startsWith("/guide/") ||
        location.pathname === "/calcolo-termini-impugnazione-licenziamento" ||
        location.pathname === "/valutatore-rischio-licenziamento";
    const onLight = scrolled || !hasDarkHero;

    const handleAnchor = (href) => {
        setOpen(false);
        if (!isHome) {
            window.location.href = `/${href}`;
        }
    };

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-white/92 backdrop-blur-xl border-b border-slate-200/80 shadow-[0_4px_30px_-12px_rgba(11,19,43,0.15)]"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="logo-link"
                    className="flex items-center gap-3 group"
                >
                    <div className="w-10 h-10 border border-gold flex items-center justify-center bg-navy">
                        <span className="font-serif text-gold text-xl leading-none">MC</span>
                    </div>
                    <div className="leading-tight">
                        <div className={`font-serif text-lg ${onLight ? "text-navy" : "text-white"}`}>
                            Avv. Marco Candido
                        </div>
                        <div className={`text-[10px] uppercase tracking-[0.25em] ${onLight ? "text-gold-deep" : "text-gold"}`}>
                            Diritto del Lavoro
                        </div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-10">
                    {NAV_LINKS.map((link) => {
                        const testId = `nav-link-${link.href.replace(/[#/]/g, "").replace(/-+/g, "-").replace(/^-|-$/g, "") || "home"}`;
                        const classes = `text-sm font-semibold tracking-wide transition-colors duration-200 hover:text-gold relative group ${
                            onLight
                                ? "text-navy"
                                : "text-white [text-shadow:_0_1px_8px_rgba(0,0,0,0.6)]"
                        }`;
                        if (link.isPage) {
                            return (
                                <Link
                                    key={link.href}
                                    to={link.href}
                                    data-testid={testId}
                                    className={classes}
                                >
                                    {link.label}
                                    <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-gold transition-all duration-300 group-hover:w-full" />
                                </Link>
                            );
                        }
                        return (
                            <a
                                key={link.href}
                                href={isHome ? link.href : `/${link.href}`}
                                onClick={() => handleAnchor(link.href)}
                                data-testid={testId}
                                className={classes}
                            >
                                {link.label}
                                <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-gold transition-all duration-300 group-hover:w-full" />
                            </a>
                        );
                    })}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <a
                        href="tel:0523468737"
                        data-testid="header-phone-link"
                        className={`flex items-center gap-2 text-sm font-semibold ${
                            onLight
                                ? "text-navy"
                                : "text-white [text-shadow:_0_1px_8px_rgba(0,0,0,0.6)]"
                        }`}
                    >
                        <Phone size={16} className="text-gold" />
                        0523 468737
                    </a>
                    <a
                        href={isHome ? "#contatti" : "/#contatti"}
                        data-testid="header-cta-contact"
                        className="btn-gold !py-2.5 !px-5 !text-xs"
                    >
                        Prenota Consulenza
                    </a>
                </div>

                <button
                    onClick={() => setOpen(!open)}
                    data-testid="mobile-menu-toggle"
                    aria-label="Apri menu"
                    className={`lg:hidden p-2 ${onLight ? "text-navy" : "text-white"}`}
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden bg-white border-t border-slate-200" data-testid="mobile-menu">
                    <div className="px-6 py-6 flex flex-col gap-5">
                        {NAV_LINKS.map((link) => {
                            const testId = `mobile-nav-${link.href.replace(/[#/]/g, "").replace(/-+/g, "-").replace(/^-|-$/g, "") || "home"}`;
                            if (link.isPage) {
                                return (
                                    <Link
                                        key={link.href}
                                        to={link.href}
                                        onClick={() => setOpen(false)}
                                        data-testid={testId}
                                        className="text-navy text-base font-medium"
                                    >
                                        {link.label}
                                    </Link>
                                );
                            }
                            return (
                                <a
                                    key={link.href}
                                    href={isHome ? link.href : `/${link.href}`}
                                    onClick={() => handleAnchor(link.href)}
                                    data-testid={testId}
                                    className="text-navy text-base font-medium"
                                >
                                    {link.label}
                                </a>
                            );
                        })}
                        <div className="pt-3 border-t border-slate-200 flex flex-col gap-3">
                            <a href="tel:0523468737" className="flex items-center gap-2 text-navy text-sm">
                                <Phone size={16} className="text-gold" /> 0523 468737
                            </a>
                            <a
                                href={isHome ? "#contatti" : "/#contatti"}
                                onClick={() => setOpen(false)}
                                className="btn-gold w-full"
                            >
                                Prenota Consulenza
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
